import type { Breadcrumb, Event } from '@sentry/browser'

/**
 * What the dashboard keeps out of Sentry, and where it lets Sentry's trace headers go (ORB-1086).
 *
 * The Room tab puts a recording straight into Blob storage through a write SAS (`…&sig=…&comp=block`), and the SDK
 * records every fetch's full URL: in breadcrumbs, which go out with the next error and which Replay turns into its
 * network spans, and in http.client span attributes. Whoever reads those could write that blob until the SAS
 * expires, so a URL carrying a SAS signature loses its query string before Sentry keeps it.
 */

/** A query string carrying a SAS signature, from its `?` to where the URL ends. */
const SAS_QUERY = /\?[^\s"'<>#]*\bsig=[^\s"'<>#]*/gi

/** `text` with the query string cut from every URL in it that carries a SAS signature. */
export function withoutSasQuery(text: string): string {
    return text.replace(SAS_QUERY, '')
}

function scrubValues<T extends Record<string, unknown>>(data: T): T {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, typeof value === 'string' ? withoutSasQuery(value) : value])) as T
}

/** `beforeBreadcrumb`: a fetch breadcrumb keeps its URL in `data.url`. Replay builds its network spans from what this returns. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    return {
        ...breadcrumb,
        ...(breadcrumb.message !== undefined && { message: withoutSasQuery(breadcrumb.message) }),
        ...(breadcrumb.data && { data: scrubValues(breadcrumb.data) }),
    }
}

/** `beforeSendSpan`: an http.client span keeps the full URL in its `url`, `http.url` and `url.full` attributes, and the query in `http.query`. */
export function scrubSpan<T extends { description?: string; data?: Record<string, unknown> }>(span: T): T {
    return {
        ...span,
        ...(span.description !== undefined && { description: withoutSasQuery(span.description) }),
        ...(span.data && { data: scrubValues(span.data) }),
    }
}

/** `beforeSend` and `beforeSendTransaction`: the request an event describes. */
export function scrubEvent<T extends Event>(event: T): T {
    const request = event.request
    if (!request) return event
    const query = request.query_string
    return {
        ...event,
        request: {
            ...request,
            ...(request.url !== undefined && { url: withoutSasQuery(request.url) }),
            ...(typeof query === 'string' && /(^|&)sig=/i.test(query) && { query_string: undefined }),
        },
    } as T
}

/**
 * Where Sentry may add its `sentry-trace` and `baggage` headers: our own API only, never the storage host a SAS
 * points at. Sentry finds a string target anywhere in the URL, query included, and tests a pattern against the whole
 * URL (and a same-origin request's path), so every target is anchored at the start.
 */
export function tracePropagationTargets(apiBase: string | undefined): RegExp[] {
    const targets = [/^https?:\/\/localhost[:/]/, /^\/api\//]
    if (!apiBase) return targets
    try {
        const host = new URL(apiBase).host.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
        return [...targets, new RegExp(`^https?://${host}/`)]
    } catch {
        return targets
    }
}
